import { useEffect, CSSProperties } from "react";
import { useAgTechState } from "@agtech/core/utilities/AgTechUtilities";
import { useAgTechPopUpContext } from "@agtech/core/functions/AgTechAppPopUps";
import { AgTechButton } from "../../../Buttons/AgTechButtons";
import AgTechVirtualizedList from "../../../Lists/AgTechVirtualizedList";
import AgTechNoRecordsPage from "../../../Pages/Error/AgTechNoRecordsPage";
import { IAgTechSelectionItem, AgTechSelectionControlProps, AgTechSelectionControlState, AgTechSelectionControlRenderingProps, AgTechSelectionControlItemCreationProps, AgTechSelectionControlEventHandlers } from "./AgTechCoreSelectionControlData";
import { logDev } from "@agtech/core/logging/AgTechLogger";
import AgTechQueryLoadingSurface from "@agtech/core/data/queries/AgTechQueryLoadingSurface";

const SELECTION_POPUP_KEYS_TO_IGNORE = [
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'Shift',
    'Tab'
]

export type AgTechSelectionPopUpProps<TItem extends IAgTechSelectionItem> = AgTechSelectionControlProps<TItem> & {
    selectionRef: React.RefObject<HTMLInputElement>
}

export const SelectionPopUpHeaderHeight = 32;

export const AgTechSelectionPopUp = <TItem extends IAgTechSelectionItem>(props: AgTechSelectionPopUpProps<TItem>) => {
    return (
        <div className='bg-white rounded flex' style={{ height: props.content.height, width: props.content.width }}>
            <AgTechQueryLoadingSurface query={props.items.itemsQuery}>
                {(items) => {
                    const filteredItems = props.items.filterItems ? items.filter(props.items.filterItems) : items;

                    return (
                        <AgTechSelectionPopUpBody
                            {...props}
                            popupItems={filteredItems}
                        />
                    )
                }}
            </AgTechQueryLoadingSurface>
        </div>
    )
}

const AgTechSelectionPopUpBody = <TItem extends IAgTechSelectionItem>(
    props: AgTechSelectionPopUpProps<TItem> & {
        popupItems: TItem[]
    }
) => {
    const selectedItemId = props.items.getSelectedItemId();

    const [popupState, setPopupState] = useAgTechState<AgTechSelectionControlState<TItem>>({
        allItems: props.popupItems,
        visibleSelectionItems: props.popupItems,
        activeItem: selectedItemId ? props.popupItems.find(item => item.id.toString() === selectedItemId) : undefined,
        activeItemListIndex: selectedItemId ? props.popupItems.findIndex(item => item.id.toString() === selectedItemId) : -1,
        freeText: props.items.freeText
    });

    useEffect(() => {
        setPopupState(curr => ({
            ...curr,
            activeItem: selectedItemId ? props.popupItems.find(item => item.id.toString() === selectedItemId) : undefined,
            activeItemListIndex: selectedItemId ? props.popupItems.findIndex(item => item.id.toString() === selectedItemId) : -1
        }));
    }, [selectedItemId]);

    const selectionEventHandlers = useSelectionPopUpEventHandlers({
        ...props,
        popupState,
        updatePopUpState: setPopupState
    });

    return (
        <div className='row-based overflow-hidden stretched rounded-inherit flex' style={props.content.styles}>
            <div className="column-based-vertically-centered px-3 bg-white border-b flex">
                {props.content.header({
                    state: popupState.current,
                    handlers: selectionEventHandlers
                })}
            </div>
            <div className="flexed scrollable flex">
                {props.content.body({
                    state: popupState.current,
                    handlers: selectionEventHandlers
                })}
            </div>
            {props.content.footer ? (
                <div className="column-based-vertically-centered px-4 py-1 border-t flex">
                    {props.content.footer({
                        state: popupState.current,
                        handlers: selectionEventHandlers
                    })}
                </div>
            ): null}
        </div>
    )
    // : (
    //     <div className='row-based overflow-hidden rounded-inherit py-5 flexed flex'>
    //         <AgTechNoRecordsPage
    //             message={props.items.itemCreationConfig?.noRecordsMessage ?? 'No options were found'}
    //             promptClasses='mt-1'
    //             action={props.items.itemCreationConfig?.creationButtonAction &&
    //                 props.items.itemCreationConfig?.creationButtonText ? {
    //                 prompt: props.items.itemCreationConfig?.noRecordsPrompt,
    //                 button: () => (
    //                     <AgTechButton 
    //                         text={props.items.itemCreationConfig?.creationButtonText}
    //                         icon='fas fa-plus'
    //                         classes='button-highlight'
    //                         action={props.items.itemCreationConfig?.creationButtonAction}
    //                     />
    //                 )
    //             } : undefined}
    //         />
    //     </div>
    // );
}

export type AgTechSelectionListHeaderProps<TItem extends IAgTechSelectionItem> = AgTechSelectionControlRenderingProps<TItem> & {
    title?: string,
    content?: () => React.ReactNode,
    creationConfig?: AgTechSelectionControlItemCreationProps<TItem>,
    styles?: CSSProperties,
}

export const AgTechSelectionListHeader = <TItem extends IAgTechSelectionItem>(props: AgTechSelectionListHeaderProps<TItem>) => {
    return (
        <div
            className='wide column-based-vertically-centered py-2 flex'
            onClick={e => e.stopPropagation()}
        >
            {props.title ? (
                <h1 className='font-semibold font-size-small px-3'>{props.title}</h1>
            ) : null}
            { props.content ? (
                <div className='ml-auto pr-2 flex'>
                    {props.content ? props.content() : null}
                </div>
            ) : props.creationConfig ? (
                <AgTechButton
                    classes='button-link ml-auto pr-[0px]'
                    height={20}
                    icon='fas fa-plus'
                    text={props.creationConfig.creationButtonText}
                    action={async e => {
                        if (props.creationConfig) {
                            await (props.creationConfig.creationButtonAction(e)
                                .then(createdItem => {
                                    props.handlers.onItemSelected(createdItem);
                                }));
                        }
                    }}
                />
            ) : null}
        </div>
)
}

export type AgTechSelectionListProps<TItem extends IAgTechSelectionItem> = AgTechSelectionControlRenderingProps<TItem> & {
    itemHeight: number,
    item: (item: TItem) => React.ReactNode
}

export const AgTechSelectionListBody = <TItem extends IAgTechSelectionItem>(
    props: AgTechSelectionListProps<TItem>
) => {
    return (
        <AgTechVirtualizedList
            items={props.state.visibleSelectionItems}
            itemHeight={props.itemHeight}
            selectedItemIndex={props.state.activeItemListIndex}
            itemRow={(item: TItem, index: number) => {
                const isSelectedItem = index === props.state.activeItemListIndex;                    
                const itemDotColor = isSelectedItem ? 'font-color-highlight' : 'font-color-tertiary';
                const itemBackgroundColor = index % 2 === 0 ? 'bg-lightest-gray' : '';

                const itemSelectionClass = isSelectedItem
                    ? 'left-bordered-highlight bg-highlight-light'
                    : 'left-bordered-transparent hover-bg-highlight-lightest';

                return (
                    <div
                        className={`wide bottom-bordered table-bordered column-based-vertically-centered px-3 clickable non-scrollable flex ${itemSelectionClass} ${itemBackgroundColor}`}
                        style={{ height: props.itemHeight }}
                        onClick={() => props.handlers.onItemSelected(item)}
                    >
                        <i className={`fas fa-circle mr-3-half ${itemDotColor}`} style={{ fontSize: 9 }} />
                        {props.item(item)}
                    </div>
                )
            }}
            noRecordsConfig={{
                message: 'No items',
            }}
            classes={`wide`}
        />
    )
}

const useSelectionPopUpEventHandlers = <TItem extends IAgTechSelectionItem>(props: AgTechSelectionPopUpProps<TItem> & {
    popupState: React.MutableRefObject<AgTechSelectionControlState<TItem>>,
    updatePopUpState: (func: (currState: AgTechSelectionControlState<TItem>) => AgTechSelectionControlState<TItem>) => void
}): AgTechSelectionControlEventHandlers<TItem> => {
    const popupContext = useAgTechPopUpContext();

    useEffect(() => {
        if (props.selectionRef.current) {
            props.selectionRef.current.onkeydown = handleKeyDown;
            props.selectionRef.current.onkeyup = handleKeyUp;
        }

        return () => {
            if (props.selectionRef.current) {
                props.selectionRef.current.onkeydown = null;
                props.selectionRef.current.onkeyup = null;
            }
        }
    }, []);

    const moveSelectedItemUp = () => {
        let updatedListIndex = props.popupState.current.activeItemListIndex - 1;
        updatedListIndex = updatedListIndex < 0 ? 0 : updatedListIndex;

        const updatedActiveItem = props.popupState.current.visibleSelectionItems[updatedListIndex];

        props.updatePopUpState(curr => ({
            ...curr,
            activeItem: updatedActiveItem,
            activeItemListIndex: updatedListIndex
        }));

        props.items.onItemSelected(updatedActiveItem);
    }

    const moveSelectedItemDown = () => {
        const numberOfVisibleItems = props.popupState.current.visibleSelectionItems.length;

        let updatedListIndex = props.popupState.current.activeItemListIndex + 1;
        updatedListIndex = updatedListIndex >= numberOfVisibleItems ? numberOfVisibleItems - 1 : updatedListIndex;

        const updatedActiveItem = props.popupState.current.visibleSelectionItems[updatedListIndex];

        logDev('AgTechSelectionPopup', 'moveSelectedItemDown', updatedListIndex, updatedActiveItem);

        props.updatePopUpState(curr => ({
            ...curr,
            activeItem: updatedActiveItem,
            activeItemListIndex: updatedListIndex
        }));

        props.items.onItemSelected(updatedActiveItem);
    }

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            moveSelectedItemUp();                    
        }
        else if (e.key === 'ArrowDown') {
            e.preventDefault();
            moveSelectedItemDown();
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            
            if (props.isOpen) {
                e.stopPropagation();
            }
            
            if (props.popupState.current.visibleSelectionItems.length === 1) {
                e.stopPropagation();

                // If only one item is visible in the dropdown and enter has been clicked, select that item
                onSelectionItemSelected(props.popupState.current.visibleSelectionItems[0]);
            }
            else if (props.popupState.current.activeItem) {
                e.stopPropagation();

                onSelectionItemSelected(props.popupState.current.activeItem);
            }
        }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
        if (SELECTION_POPUP_KEYS_TO_IGNORE.indexOf(e.key) >= 0) { return; }

        const updatedSelectionSearchValue = props.selectionRef.current?.value ?? '';

        if (updatedSelectionSearchValue) {
            props.updatePopUpState(curr => ({
                ...curr,
                activeItemListIndex: -1,
                visibleSelectionItems: curr.allItems.filter(item => {
                    return props.items.searchItems(item, updatedSelectionSearchValue);
                })
            }));
        }
        else if (props.popupState.current.activeItem) {
            const activeItemIndex = props.popupState.current.allItems.findIndex(item => item.id === props.popupState.current.activeItem?.id);

            props.updatePopUpState(curr => ({
                ...curr,
                visibleSelectionItems: curr.allItems,
                activeItemListIndex: activeItemIndex,
            }));
        }
        else {
            props.updatePopUpState(curr => ({
                ...curr,
                visibleSelectionItems: curr.allItems,
                activeItemListIndex: -1
            }));
        }
    }

    const onSelectionItemSelected = async (item: TItem) => {
        // If the selected item doesn't exist in the current list of items, the item was
        // created via the popup. Add the selected item to the list of all items.
        if (!props.popupState.current.allItems.some(i => i.id === item.id)) {
            props.updatePopUpState(curr => {
                const updatedItems = [...curr.allItems, item];

                return {
                    ...curr,
                    allItems: updatedItems,
                    activeItem: item,
                    activeItemListIndex: curr.allItems.length
                };
            });
        }

        await props.items.onItemSelected(item);

        popupContext.closePopUp();
    }

    return {
        onItemSelected: onSelectionItemSelected,
        onItemsUpdated: updatedItems => props.updatePopUpState(curr => ({
            ...curr,
            visibleSelectionItems: updatedItems
        }))
    }
}